import { APIPath } from '../../api/API';
import { createReportingAPIs } from '../../api/impl/createReportingAPIs';
import { createBrowserReplicantActionAPI } from '../../api/impl/ReplicantAPIImplBrowser';
import type { Fetcher } from '../../api/impl/utils/Fetcher';
import { ReplicantBrowserFetcher } from '../../api/impl/utils/impl/ReplicantBrowserFetcher';
import { DelayedAction } from '../../api/ReplicantAPI';
import type { PostAnalyticsFN } from '../../common/Analytics';
import { ErrorReporter } from '../../common/ErrorReporter';
import type { ABTestDynamicConfig } from '../../common/ReplicantABTests';
import type { Entry } from '../../db/DB';
import createLocalStorageDB from '../../db/impl/LocalStorageDB';
import { ReplicantError } from '../../Errors';
import { createSharedStateIndexDBsLocalStorage } from '../../indexdb/impl/createSharedStateIndexDBs';
import createIndexDBLocalStorage from '../../indexdb/impl/IndexDBInLocalStorage';
import { Replicant, ReplicantConfig } from '../../ReplicantConfig';
import { ReplicantPlatform } from '../../ReplicantPlatform';
import { createMockScheduledActionsQueue } from '../../scheduledActionsQueue';
import { createStubNarrowcastAPIClient } from '../../server/NarrowcastAPIClient/createStubNarrowcastAPIClient';
import { CreateOrLoginOutput, ServerReplicant, ServerReplicantDevOptions } from '../../server/ServerReplicant';
import type { WithMeta } from '../../systemStateFields';
import { createStubAirtableClient } from '../../utils/Airtable';
import { getAppName } from '../../utils/AppName';
import { getNamespacedValue } from '../../utils/ObjUtils';
import { createStubTwilioClient } from '../../utils/Twilio';
import { generateRandomId } from '../../utils/Utils';
import StubMetrics from '../lambda/Metrics/impl/StubMetrics';
import Router from '../lambda/Router';
import StubAssetUploader from './StubAssetUploader';
import StubLineRichMenuAPIClient from './StubLineRichMenuAPIClient';

/**
 * A hard-coded JWT for accessing dev-stage external services in local
 * development without having to include crypto code in browser bundle.
 *
 * Signed with the development key using the following payload (expires in 100 years):
 * `{ playerId: 'dummy-user', appId: 'dummy-app', exp: 4740965940 }`
 */
const dummyToken =
    'eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJwbGF5ZXJJZCI6ImR1bW15LXVzZXIiLCJhcHBJZCI6ImR1bW15LWFwcCIsImV4cCI6NDc0MDk2NTk0MCwiaWF0IjoxNTg3MzY2MDE2fQ.JEA1IWdxQHkICDfEq6UCyInpvlNNxZ__2ZPP8CxU8XX0OSzcDOL8XOM5X72mgNmooXX4ll218Wu_Tqj0lc8WUQd66FUZL9GjYtM-v0Yg73Ky3gwqjzEOwW_Z5803iVKW8MotZ0YDoNLxtPRCeinzUbDh0qCU5OHkKaY-nXy-_rRDXXxGKmNqPN41Ox7srM9DMdheDdAjH3vmZWuV_QgAiRHjE-dv8i1KP4_PFKflOVhUYnWfBeyUhCXai1hHjCTjmij3Q1vB912NOodUgpvY5IQL_zVWGth-niCdMaQOeIB4e2x9zuV1788pfMEmzWdhAawyH1BVQvAkAaVA_inJfA';

export function createBrowserReplicantFetch<T extends Replicant>(opts: {
    config: ReplicantConfig<T>;
    fetcher?: Fetcher;
    platform: ReplicantPlatform;
    latency?: number;
    storageOverride?: Storage;
    responseOverride?: (body: object | null, status: number, headers: string[][]) => Response;
    dateNow?: () => number;
    errorReporter?: ErrorReporter;
    postAnalytics?: PostAnalyticsFN;
    storageKeyPrefix?: string;
}): typeof fetch {
    const prefix = getAppName(opts.config) + (opts.storageKeyPrefix ? '_' + opts.storageKeyPrefix : '');

    const db = createLocalStorageDB<T['state']>({ storage: opts.storageOverride, prefix });
    const indexDB = opts.config.computedProperties
        ? createIndexDBLocalStorage('main_index', opts.config.computedProperties, {
              storage: opts.storageOverride,
              prefix,
          })
        : undefined;

    const sharedStateIndexDBs =
        opts.config.sharedStates &&
        createSharedStateIndexDBsLocalStorage({
            indexName: 'main_index',
            sharedStates: opts.config.sharedStates,
            prefix,
            storageOverride: opts.storageOverride,
        });

    const options: Partial<ServerReplicantDevOptions> = {
        chatbotAssetUrls: opts.config.chatbot?.assets,
        errorReporter: opts.errorReporter,
        postActivePushNotification: () => Promise.resolve({ success: true }),
        postAnalytics: opts.postAnalytics,
        richMenuAPIClient: new StubLineRichMenuAPIClient(),
    };

    if (opts.dateNow) {
        options.dateNow = opts.dateNow;
    }

    // Airtable is disabled in offline mode: Airtable's API has strict rate limits and in
    // offline mode there's no backend to use as a shared cache between clients.
    const stubAirtableClient = createStubAirtableClient('Airtable is disabled in offline mode');

    // A/B tests are always activated and rolled out at 100% in offline mode:
    const mockAbTestDynamicConfig = Object.keys(opts.config.ruleset?.abTests ?? {}).reduce(
        (acc, testId) => ({ ...acc, [testId]: { active: true, rollOut: 1 } }),
        {} as { [testId: string]: ABTestDynamicConfig },
    );
    void db.abTests.set(mockAbTestDynamicConfig);

    const serverReplicant = new ServerReplicant(opts.config, {
        airtable: stubAirtableClient,
        assetUploader: new StubAssetUploader(),
        createActionAPI: async (...args) => createBrowserReplicantActionAPI(...args),
        crypto: {} as any, // Unused in offline mode
        db,
        encodeToken: () => dummyToken,
        fetcher: opts.fetcher ?? new ReplicantBrowserFetcher(),
        gzipToBase64: () => '', // Stub -- the browser does not zip
        indexDB,
        narrowcastAPIClient: createStubNarrowcastAPIClient('LINE chatbot broadcast is disabled in offline mode'),
        options,
        platform: opts.platform,
        scheduledActionsQueue: createMockScheduledActionsQueue(),
        sharedStateIndexDBs,
        twilio: createStubTwilioClient('Twilio is disabled in offline mode'),
        webPush: { sendNotification: () => Promise.reject(new Error('WebPush disabled in offline mode.')) },

        // Web playable mode is not enabled in offline Replicant:
        generateWebPlayerAuthToken: () => '',
        generateWebPlayerId: () => '',
    });

    const router = new Router({
        errorReporter: serverReplicant.errorReporter,
        metrics: new StubMetrics(),

        // Stub -- the browser does not zip
        gzipToBase64: () => ({ body: '', byteLength: 0 }),
    });

    // After context switch hook is no-op in offline mode:
    router.post(APIPath.AFTER_CONTEXT_SWITCH, async ({ respond }) =>
        respond({ payload: { body: JSON.stringify({}), statusCode: 200 } }),
    );

    router.post(APIPath.READ_KEY_VALUES, async ({ body, respond }) => {
        const result = await serverReplicant.handleFetchKvPairs(body.keys);

        return respond({
            payload: {
                statusCode: 200,
                body: JSON.stringify(result),
            },
        });
    });

    router.post(APIPath.READ_INTERNAL_KEY_VALUES, async ({ body, respond }) => {
        const result = await serverReplicant.handleFetchInternalKvPairs(body.keys);

        return respond({
            payload: {
                statusCode: 200,
                body: JSON.stringify(result),
            },
        });
    });

    router.post(APIPath.LOGIN_OR_CREATE, async ({ body, respond }) => {
        const result: CreateOrLoginOutput<T['state']> = await serverReplicant.handleCreateOrLogin({
            id: body.id,
            sessionId: body.sid,
            onLoginActionArgs: body.onLoginActionArgs,
            prefetchKeys: body.prefetchKeys,
            prefetchInternalKeys: body.prefetchInternalKeys,
            skipOnLoginAction: !!body.skipOnLoginAction,
        });

        return respond({ payload: { statusCode: 200, body: JSON.stringify(result) } });
    });

    router.post(APIPath.REPLICATE, async ({ body, respond }) => {
        const results = await serverReplicant.handleReplicate({
            abTestsDynamicConfig: body.abTestsDynamicConfig,
            clientMetainfo: body.clientMetainfo,
            id: body.id,
            queue: body.queue,
            clientRandomSeed: body.clientRandomSeed,
            rev: body.rev,
            requestedProfileIds: body.requestedProfileIds,
            consistentFetchIds: body.consistentFetchIds,
            sessionId: body.sid,
            clientRequestId: body.crqid,
        });

        return respond({ payload: { statusCode: 200, body: JSON.stringify(results) } });
    });

    router.post(APIPath.FETCH_STATES, async ({ body, respond }) => {
        const results = await serverReplicant.handleFetchStates(body.ids, body.friendRevs, body.consistentFetchIds);

        return respond({ payload: { statusCode: 200, body: JSON.stringify(results) } });
    });

    router.post(APIPath.ASYNC_GETTER, async ({ body, respond }) => {
        const { args, id, name, sid } = body;

        async function getOwnEntry() {
            const entry = (await serverReplicant.getReadOnlyEntry(id))?.entry;

            if (!entry) {
                throw new ReplicantError(
                    'Own entry not found inside asyncGetter',
                    'server_error',
                    'async_getter_error',
                    'fatal',
                );
            }

            return entry as Entry<WithMeta<any, any, any>>;
        }

        // We may have configured a clock offset for non-prod environments.
        // So, we need to first fetch the offset from the metainfo, so we can apply it in date.now()
        const clockOffset = (await getOwnEntry()).metainfo.clockOffset || 0;

        const delayedActions: DelayedAction[] = [];
        const reportingAPIs = createReportingAPIs({ apiAccess: serverReplicant, delayedActions, id, sessionId: sid });

        const asyncGetter = getNamespacedValue(serverReplicant.asyncGetters.asyncGetters, name);

        if (!asyncGetter) {
            throw Error(`Async getter not found: ${name}`);
        }

        const result = await asyncGetter(args, {
            getOwnEntry,
            id,
            sessionId: sid,
            clockOffset,
            consistentFetchIds: body.consistentFetchIds,
            ...reportingAPIs,
        });

        // Execute delayed actions from the reporting APIs
        await serverReplicant.resolveDelayedActions(delayedActions);

        return respond({ payload: { statusCode: 200, body: JSON.stringify({ result }) } });
    });

    router.post(APIPath.UPLOAD_USER_ASSET, async ({ body, respond }) => {
        const results = await serverReplicant.handleUserAssetUpload(body);
        return respond({ payload: { statusCode: 200, body: JSON.stringify(results) } });
    });

    router.post(APIPath.WRITE_KEY_VALUES, async ({ body, respond }) => {
        const results = await serverReplicant.handleWriteKvPairs(body.pairs, body.opts);
        return respond({ payload: { statusCode: 200, body: JSON.stringify(results) } });
    });

    router.post(APIPath.SOCIAL_GRAPH_UPDATE_SELF, async ({ body, respond }) => {
        await serverReplicant.handleSocialGraphUpdateSelf({
            playerId: body.id,
            nonPlayerFriendIds: body.nonPlayerFriendIds,
            meta: body.meta,
        });
        return respond({ payload: { statusCode: 200, body: JSON.stringify('ok') } });
    });

    router.post(APIPath.SOCIAL_GRAPH_TRACK_INTERACTIONS, async ({ body, respond }) => {
        await serverReplicant.handleSocialGraphTrackInteractions({
            senderId: body.id,
            interactions: body.interactions,
        });
        return respond({ payload: { statusCode: 200, body: JSON.stringify('ok') } });
    });

    router.post('token', async ({ body, respond }) => {
        const result = await serverReplicant.handleGetToken(body.id);
        return respond({ payload: { statusCode: 200, body: JSON.stringify(result) } });
    });

    router.post('testUsers', async ({ body, respond }) => {
        const result = await serverReplicant.handleSetTestUsers(body.id, body.states);
        return respond({ payload: { statusCode: 200, body: JSON.stringify(result) } });
    });

    const routerListener = router.listen();

    async function browserFetch(url: RequestInfo | URL, init?: RequestInit) {
        // Symmetric latency.
        const latency = opts.latency || 0;

        await delay(latency / 2);

        if (!init?.method) {
            throw new Error('Browser replicant could not determine HTTP method.');
        }

        const headers: { [header: string]: string } = {};

        const initHeaders = init.headers as { [key: string]: string };
        for (const key in initHeaders) {
            headers[key] = initHeaders[key]!;
        }

        const bodyAsString = init.body?.toString();

        const queryStringParameters: { [param: string]: string } = {};

        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        const [, urlString] = /.*?((\/v|latest).*)/g.exec(url.toString())!;
        const [path, args] = urlString!.split('?') as [string, string];
        if (args) {
            args.split('&').forEach((arg) => {
                const [key, value] = arg.split('=') as [string, string];
                queryStringParameters[key] = value;
            });
        }

        const response = await routerListener(
            {
                body: bodyAsString || '',

                headers,
                multiValueHeaders: {},

                httpMethod: init.method,
                isBase64Encoded: false,

                path,

                queryStringParameters,

                //
                requestContext: { elb: { targetGroupArn: '' } },
            },
            {
                callbackWaitsForEmptyEventLoop: false,

                functionName: 'dev-server',
                functionVersion: 'v0.0.0',

                invokedFunctionArn: '',
                memoryLimitInMB: '0',

                awsRequestId: generateRandomId(),

                logGroupName: '',
                logStreamName: '',

                getRemainingTimeInMillis: () => 0,

                done: () => void 0,
                fail: () => void 0,
                succeed: () => void 0,
            },
        );

        await delay(latency / 2);

        // Assumption is if you're proviidng an override for the localStorage, then you're not
        // running in the browser.
        const responseHeaders: [string, string][] = [['content-type', 'text/plain;charset=UTF-8']];

        for (const key in response.headers) {
            responseHeaders.push([key, response.headers[key]!.toString()]);
        }

        const createResponse = opts.responseOverride ?? createResponseBrowser;

        return createResponse(response.body ? JSON.parse(response.body) : null, response.statusCode, responseHeaders);
    }

    // Store to be reused in offline helpers (for testing and dev interface).
    browserFetch.storageOverride = opts.storageOverride;
    browserFetch.dbPrefix = getAppName(opts.config) + (opts.storageKeyPrefix ? '_' + opts.storageKeyPrefix : '');
    browserFetch.serverReplicant = serverReplicant;
    browserFetch.db = db;
    browserFetch.indexDB = indexDB;

    return browserFetch;
}

async function delay(ms: number) {
    if (ms === 0) return;

    return new Promise((res) => setTimeout(res, ms));
}

function createResponseBrowser(body: object | null, status: number, headers: [string, string][]) {
    const response = new Response(JSON.stringify(body), {
        status,
        headers,
    });
    return response;
}
